import React from 'react';
import { motion } from 'framer-motion';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useLibrary } from '../lib/library';

/**
 * Small icon toggle. Click = add/remove episode from My Library (localStorage).
 * Props:
 *   ep      — episode object (needs .id and .title)
 *   size    — 'sm' | 'md' | 'lg'  (default 'md')
 *   variant — 'ghost' | 'solid'   (default 'ghost')
 *   testId  — override data-testid
 */
const SIZES = {
  sm: { btn: 'w-8 h-8', icon: 'w-3.5 h-3.5' },
  md: { btn: 'w-10 h-10', icon: 'w-4 h-4' },
  lg: { btn: 'w-11 h-11', icon: 'w-5 h-5' },
};

const BookmarkButton = ({ ep, size = 'md', variant = 'ghost', testId }) => {
  const { has, toggle } = useLibrary();
  if (!ep || !ep.id) return null;
  const saved = has(ep.id);
  const s = SIZES[size] || SIZES.md;

  const base =
    variant === 'solid'
      ? 'bg-black/70 backdrop-blur-md border border-white/10 hover:border-[#EF5B5B]/60'
      : 'glass hover:border-[#EF5B5B]/60 border border-white/10';

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const nowSaved = toggle(ep.id);
    toast[nowSaved ? 'success' : 'message'](
      nowSaved ? 'Saved to My Library' : 'Removed from My Library',
      {
        description: ep.title,
        action: nowSaved
          ? { label: 'View', onClick: () => (window.location.href = '/library') }
          : undefined,
      }
    );
  };

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 600, damping: 22 }}
      aria-label={saved ? 'Remove from My Library' : 'Save to My Library'}
      aria-pressed={saved}
      data-testid={testId || `bookmark-${ep.id}`}
      className={`${s.btn} ${base} inline-flex items-center justify-center rounded-full smooth ${
        saved ? 'text-[#EF5B5B]' : 'text-gray-300 hover:text-white'
      }`}
    >
      {saved ? (
        <BookmarkCheck className={s.icon} fill="currentColor" strokeWidth={2} />
      ) : (
        <Bookmark className={s.icon} strokeWidth={2} />
      )}
    </motion.button>
  );
};

export default BookmarkButton;
